import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Phone, Mail, Send, Expand, X } from "lucide-react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import SchemaOrg from "@/components/SchemaOrg";

const contactFormSchema = z.object({
  name: z.string().min(2, { message: "El nombre debe tener al menos 2 caracteres" }),
  email: z.string().email({ message: "Por favor, introduce un email válido" }),
  phone: z.string().optional(),
  subject: z.string().min(3, { message: "Por favor, introduce un asunto" }),
  message: z.string().min(10, { message: "El mensaje debe tener al menos 10 caracteres" }),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

function LocationMap({ mapUrl, title }: { mapUrl: string; title: string }) {
  return (
    <iframe
      src={mapUrl}
      width="100%"
      height="100%"
      style={{ border: 0 }}
      allowFullScreen
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      title={title}
    ></iframe>
  );
}

export default function Contacto() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const locations = [
    {
      name: "Oficinas (Madrid)",
      address: "Paseo de la Castellana 259C, planta 18",
      city: "28046, Madrid (España)",
      phone: "",
      mapUrl: "https://maps.google.com/maps?q=Paseo+de+la+Castellana+259C,+Madrid&t=&z=13&ie=UTF8&iwloc=&output=embed"
    },
    {
      name: "Centro de reacondicionado / Exposición zona norte (Venta de Baños)",
      address: "Calle Tren Ter, 199",
      city: "34200 Venta de Baños (Palencia)",
      company: "INNOTEC FOOD EQUIPMENT S.L.",
      phone: "",
      mapUrl: "https://maps.google.com/maps?q=Calle+Tren+Ter+199,+34200+Venta+de+Baños,+Palencia&t=&z=13&ie=UTF8&iwloc=&output=embed"
    },
    {
      name: "Almacén N-620",
      address: "Ctra. N-620 Palencia a Valladolid, Km 4,5 (Naves 5 y 7)",
      city: "34190 Villamuriel de Cerrato (Palencia)",
      company: "",
      phone: "",
      mapUrl: "https://maps.google.com/maps?q=Ctra+N620+Palencia+a+Valladolid+Km+4,5,+34190+Villamuriel+de+Cerrato&t=&z=13&ie=UTF8&iwloc=&output=embed"
    },
    {
      name: "Almacén N-611",
      address: "Carretera Nacional 611, Km 2 (Nave 11)",
      city: "34190 Villamuriel de Cerrato (Palencia)",
      company: "",
      phone: "",
      mapUrl: "https://maps.google.com/maps?q=Carretera+Nacional+611+km+2,+34190+Villamuriel+de+Cerrato&t=&z=13&ie=UTF8&iwloc=&output=embed"
    }
  ];

  const [activeLocation, setActiveLocation] = useState(0);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMapExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (mapRef.current && !mapRef.current.contains(event.target as Node)) {
        setIsMapExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMapExpanded]);

  useEffect(() => {
    if (isMapExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMapExpanded]);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
  });

  async function onSubmit(data: ContactFormValues) {
    setIsSubmitting(true);

    try {
      // Aquí iría la lógica para enviar el formulario
      console.log("Datos del formulario:", data);

      // Simulamos una petición
      await new Promise(resolve => setTimeout(resolve, 1000));

      toast({
        title: "Mensaje enviado",
        description: "Nos pondremos en contacto contigo pronto.",
      });

      form.reset();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo enviar el mensaje. Por favor, inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <SEO
        title="Contacto | Innotec Food Equipment"
        description="Ponte en contacto con nuestro equipo de expertos en maquinaria para la industria alimentaria. Estamos aquí para ayudarte con cualquier consulta o proyecto."
        canonicalUrl="/contacto"
      />

      <SchemaOrg
        type="organization"
        data={{
          url: "https://www.innotecfood.eu",
          logo: "https://www.innotecfood.eu/images/logo.png",
          name: "Innotec Food Equipment",
          description: "Especialistas en maquinaria industrial para el sector alimentario. Soluciones innovadoras para procesamiento, envasado y automatización de alimentos.",
          address: {
            streetAddress: "Paseo castellana 259 C, Piso 18",
            addressLocality: "Madrid",
            postalCode: "28046",
            addressCountry: "ES"
          },
          contactPoint: {
            telephone: "+34613222510",
            email: "info@innotecfood.com"
          },
          sameAs: [
            "https://www.facebook.com/innotecfood",
            "https://www.instagram.com/innotecfood",
            "https://www.linkedin.com/company/innotecfood"
          ]
        }}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <PageHeader
            title="Contacto"
            description="Estamos aquí para ayudarte. Ponte en contacto con nuestro equipo de expertos."
            className="mb-12"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Información de contacto */}
            <div className="lg:col-span-1 space-y-8">
              <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6">Información de contacto</h3>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#FCAA0D]/10 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-[#FCAA0D]" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-white">Dirección</p>
                      <p className="mt-1 text-sm text-zinc-400">
                        Paseo castellana 259 C, Piso 18<br />
                        28046 Madrid, España
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#FCAA0D]/10 flex items-center justify-center">
                      <Phone className="h-5 w-5 text-[#FCAA0D]" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-white">Teléfono</p>
                      <a
                        href="tel:+34613222510"
                        className="mt-1 text-sm text-zinc-400 hover:text-[#FCAA0D] transition-colors"
                      >
                        (+34) 613 222 510
                      </a>
                      <br />
                      <a
                        href="tel:+34622451145"
                        className="mt-1 text-sm text-zinc-400 hover:text-[#FCAA0D] transition-colors"
                      >
                        (+34) 622 451 145
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#FCAA0D]/10 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-[#FCAA0D]" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-white">Email</p>
                      <a
                        href="mailto:info@innotecfood.com"
                        className="mt-1 text-sm text-zinc-400 hover:text-[#FCAA0D] transition-colors"
                      >
                        info@innotecfood.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-sm font-medium text-white mb-4">Horario de atención</h4>
                  <p className="text-sm text-zinc-400">
                    Lunes a Viernes: 9:00 - 18:00<br />
                    Sábados y Domingos: Cerrado
                  </p>
                </div>
              </div>
            </div>

            {/* Formulario de contacto */}
            <div className="lg:col-span-2">
              <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6">Envíanos un mensaje</h3>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-300">Nombre</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Tu nombre"
                                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#FCAA0D]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-300">Email</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="tu@email.com"
                                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#FCAA0D]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-300">Teléfono (opcional)</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Tu teléfono"
                                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#FCAA0D]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="subject"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-300">Asunto</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Asunto del mensaje"
                                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#FCAA0D]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-zinc-300">Mensaje</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="¿En qué podemos ayudarte?"
                              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#FCAA0D] min-h-[150px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-semibold"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Enviando...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <Send className="mr-2 h-4 w-4" />
                          Enviar mensaje
                        </span>
                      )}
                    </Button>
                  </form>
                </Form>
              </div>
            </div>
          </div>

          {/* Mapa y ubicaciones */}
          <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-6 lg:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <h3 className="text-2xl font-bold text-white mb-6">Nuestras Ubicaciones</h3>

                {/* Selector de ubicaciones */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {locations.map((location, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveLocation(index)}
                      className={`relative z-10 cursor-pointer px-3 py-1 text-sm rounded-full transition-colors ${
                        activeLocation === index
                          ? "bg-[#FCAA0D] text-black font-medium"
                          : "bg-white/10 text-gray-300 hover:bg-white/20"
                      }`}
                    >
                      {location.name}
                    </button>
                  ))}
                </div>

                {/* Información de la ubicación */}
                <div className="mb-4 p-4 bg-white/5 rounded-lg">
                  <a
                    href={locations[activeLocation].mapUrl.replace('output=embed', 'output=svembed')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative z-10 block cursor-pointer transition-colors group"
                  >
                    <p className="font-semibold text-white group-hover:text-[#FCAA0D]">{locations[activeLocation].name}</p>
                    <p className="text-gray-300 group-hover:text-white mt-1">{locations[activeLocation].address}</p>
                    <p className="text-gray-300 group-hover:text-white">{locations[activeLocation].city}</p>
                  </a>
                  {locations[activeLocation].company && (
                    <p className="text-gray-300 mt-2 font-semibold">{locations[activeLocation].company}</p>
                  )}
                </div>

              </div>
              <div className="lg:col-span-2">
                {/* Mapa */}
                <div className="relative h-80 lg:h-full rounded-lg overflow-hidden group">
                  <LocationMap
                    key={activeLocation}
                    mapUrl={locations[activeLocation].mapUrl}
                    title={`Mapa de ${locations[activeLocation].address}`}
                  />
                  <button
                    className="absolute bottom-2 right-2 bg-[#FCAA0D] text-black p-2 rounded-md opacity-80 hover:opacity-100 transition-opacity duration-300 flex items-center gap-1 text-sm"
                    onClick={() => setIsMapExpanded(true)}
                  >
                    <Expand className="w-4 h-4" />
                    <span>Ampliar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </motion.div>

      {/* Mapa expandido */}
      {isMapExpanded && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 md:p-8 animate-in fade-in duration-300">
          <div
            ref={mapRef}
            className="bg-[#0A0A0B] border border-[#FCAA0D]/20 rounded-xl overflow-hidden w-full max-w-4xl relative"
          >
            <div className="p-4 bg-[#FCAA0D]/10 flex justify-between items-center">
              <h3 className="text-white font-medium">{locations[activeLocation].address}</h3>
              <button
                className="text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
                onClick={() => setIsMapExpanded(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-col sm:flex-row">
              <div className="p-4 flex flex-col sm:w-1/3">
                <a
                  href={locations[activeLocation].mapUrl.replace('output=embed', 'output=svembed')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative z-10 block cursor-pointer transition-colors group"
                >
                  <p className="text-gray-300 text-sm mb-1 group-hover:text-white">{locations[activeLocation].address}</p>
                  <p className="text-gray-300 text-sm mb-4 group-hover:text-white">{locations[activeLocation].city}</p>
                </a>
                {locations[activeLocation].company && (
                  <p className="text-gray-300 text-sm font-semibold mb-4">{locations[activeLocation].company}</p>
                )}

                <div className="mt-4">
                  <p className="text-white mb-2 text-sm">Seleccionar sede:</p>
                  <div className="flex flex-wrap gap-2">
                    {locations.map((location, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveLocation(index)}
                        className={`relative z-10 cursor-pointer px-2 py-1 text-xs rounded-full transition-colors ${
                          activeLocation === index
                            ? "bg-[#FCAA0D] text-black font-medium"
                            : "bg-white/10 text-gray-300 hover:bg-white/20"
                        }`}
                      >
                        {location.name}
                      </button>
                    ))}
                  </div>
                </div>
                <a
                  href={locations[activeLocation].mapUrl.replace('output=embed', 'output=svembed')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto bg-[#FCAA0D] text-black py-2 px-4 rounded text-center mt-4 hover:bg-[#FCAA0D]/90 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Send className="w-4 h-4" />
                  <span>Abrir en Google Maps</span>
                </a>
              </div>
              <div className="sm:w-2/3 h-[300px] sm:h-auto">
                <LocationMap
                  key={activeLocation}
                  mapUrl={locations[activeLocation].mapUrl}
                  title={`Mapa de ${locations[activeLocation].address}`}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
